import React, { useState, ReactNode, useCallback, useRef, useEffect } from 'react';
import {
  Referral,
  ReferralStage,
  ReferralStatus,
  ReferralSource,
  ReferralPatient,
  ReferralTimelineEvent,
  IngestionType,
  DecisionOutcome,
  FlagSeverity,
  TransitionContextValue,
  TransitionDemoUser,
  PhoneIntakeFormData,
  FaxIntakeFormData,
  ReprocessingTask,
  ReferralDocument,
  ReferralDocumentType,
  PriorEncounter,
} from '~/types/transition';

export const TRANSITION_DEMO_USERS: TransitionDemoUser[] = [
  {
    id: 'sarah-brennan',
    name: 'Sarah Brennan',
    role: 'Referral Coordinator',
    access: ['transition'],
  },
  {
    id: 'jamie-reyes',
    name: 'Jamie Reyes',
    role: 'Care Coordinator',
    access: ['transition', 'operate'],
  },
];

// TransitionContext lives in useTransitionContext.ts so that HMR updates to this provider file
// don't create a new context object identity that breaks consumers.
import { TransitionContext } from '~/hooks/transition/useTransitionContext';
export { TransitionContext } from '~/hooks/transition/useTransitionContext';

// ==================== MOCK SCENARIO DATA ====================

const MOCK_REFERRALS: Referral[] = [
  // ── Scenario 1: Email Referral — Clean, ready to accept ──────────────────
  {
    id: 'ref-001',
    scenarioId: 'email-clean',
    stage: ReferralStage.DECISION,
    status: ReferralStatus.RECOMMENDATION_READY,
    source: ReferralSource.EMAIL,
    ingestionType: IngestionType.AUTO,
    receivedAt: '2026-04-20T08:14:00Z',
    updatedAt: '2026-04-20T08:16:22Z',
    processingComplete: true,
    patient: {
      name: 'Margaret Chen',
      dob: '1954-03-12',
      address: '4821 Maple Street',
      city: 'Denver',
      state: 'CO',
      zip: '80203',
      phone: '(303) 555-0192',
      insurance: 'Aetna',
      insuranceMemberId: 'AET-882-447731',
      diagnosisCodes: ['I50.9', 'E11.9'],
      diagnosisLabels: ['Congestive Heart Failure', 'Type 2 Diabetes'],
      requestedProgram: 'Home Health',
      referringProvider: 'Dr. Sandra Okonkwo',
      referringFacility: 'Denver Memorial Hospital',
      referringPhone: '(303) 555-0200',
      referringFax: '(303) 555-0201',
    },
    summary:
      'Margaret Chen is a 72-year-old female referred for Home Health services following a hospitalization for exacerbated congestive heart failure. She resides in Denver, CO within the active service area. Insurance is verified as Aetna (in-network). All required clinical documentation is present. She has no caregiver restrictions. AI processing is complete with no missing information.',
    flags: [],
    missingFields: [],
    decisionFactors: [
      { criterion: 'Geography', requirement: 'Denver service area', patientValue: 'Denver, CO', result: 'PASS' },
      { criterion: 'Insurance', requirement: 'In-network carrier', patientValue: 'Aetna', result: 'PASS' },
      { criterion: 'Program Fit', requirement: 'Home Health eligible', patientValue: 'Home Health', result: 'PASS' },
      { criterion: 'Clinical Acuity', requirement: 'Stable for home care', patientValue: 'Stable — CHF managed', result: 'PASS' },
      { criterion: 'Service Area', requirement: 'Active service area', patientValue: 'Denver Metro', result: 'PASS' },
    ],
    capacityInfo: {
      territory: 'Denver',
      available: true,
      availableCount: 3,
      recommendation: 'ACCEPT',
      recommendationLabel: 'Accept — 3 slots available in the Denver service area',
    },
    aiRecommendation: DecisionOutcome.ACCEPT,
    aiRecommendationReason:
      'All acceptance criteria are met. Geography, insurance, and program fit are confirmed. Capacity is available in the Denver territory. Recommend immediate acceptance.',
    timeline: [
      {
        id: 'e1-1',
        timestamp: '2026-04-20T08:14:00Z',
        type: 'RECEIVED',
        actor: 'SYSTEM',
        label: 'Referral received via Email',
        detail: 'Auto-ingested from referrals@medarrive.com inbox',
      },
      {
        id: 'e1-2',
        timestamp: '2026-04-20T08:14:05Z',
        type: 'PROCESSING',
        actor: 'AI',
        label: 'AI extraction started',
        detail: 'Parsing referral email and attachments',
      },
      {
        id: 'e1-3',
        timestamp: '2026-04-20T08:14:18Z',
        type: 'PROCESSING',
        actor: 'AI',
        label: 'Structured data extracted',
        detail: 'Patient demographics, insurance, and clinical data extracted successfully',
      },
      {
        id: 'e1-4',
        timestamp: '2026-04-20T08:14:22Z',
        type: 'PROCESSING',
        actor: 'AI',
        label: 'Validation complete — no missing fields',
      },
      {
        id: 'e1-5',
        timestamp: '2026-04-20T08:15:01Z',
        type: 'PROCESSING',
        actor: 'AI',
        label: 'Capacity checked — Denver territory',
        detail: '3 slots available',
      },
      {
        id: 'e1-6',
        timestamp: '2026-04-20T08:16:22Z',
        type: 'DECISION',
        actor: 'AI',
        label: 'AI recommendation ready: Accept',
        detail: 'All criteria met. Awaiting human confirmation.',
      },
    ],
    priorEncounters: [
      {
        id: 'pe-001-1',
        date: '2024-11-03',
        program: 'Home Health',
        outcome: 'COMPLETED',
        summary: 'Home Health episode for post-hospitalization management of congestive heart failure exacerbation. 8-week episode, skilled nursing 3×/week. Care plan goals met — no hospital readmission. Discharged from service in good standing.',
        assignedProvider: 'Sarah Novak, RN',
      },
      {
        id: 'pe-001-2',
        date: '2023-06-18',
        program: 'Home Health',
        outcome: 'COMPLETED',
        summary: 'Home Health episode following adjustment of cardiac medication regimen. 4-week episode, skilled nursing 2×/week for medication management and monitoring. Patient educated on self-monitoring for fluid retention. Discharged successfully.',
        assignedProvider: 'Marcus Lee, RN',
      },
    ],
  },

  // ── Scenario 2: Fax Referral — Broken, missing info loop ─────────────────
  {
    id: 'ref-002',
    scenarioId: 'fax-broken',
    stage: ReferralStage.PROCESSING,
    status: ReferralStatus.AWAITING_INFO,
    source: ReferralSource.FAX,
    ingestionType: IngestionType.ASSISTED,
    receivedAt: '2026-04-20T06:31:00Z',
    updatedAt: '2026-04-20T07:45:00Z',
    processingComplete: false,
    patient: {
      name: 'Robert Torres',
      dob: '1958-07-29',
      address: '112 Birchwood Ave',
      city: 'Denver',
      state: 'CO',
      zip: '80210',
      insurance: 'UnitedHealthcare',
      diagnosisCodes: ['M17.11', 'Z96.641'],
      diagnosisLabels: ['Primary osteoarthritis, right knee', 'Status post right knee replacement'],
      requestedProgram: 'Home Health',
      referringProvider: 'Dr. Alan Marsh',
      referringFacility: 'St. Luke\'s Orthopedic Center',
    },
    summary:
      'Robert Torres is a 67-year-old male referred for post-surgical Home Health following right knee replacement. Fax received but page 2 of 3 is partially illegible. Insurance member ID and referring provider phone number are missing. AI has drafted outreach to the referring facility to obtain the missing information.',
    flags: [
      {
        id: 'f2-1',
        severity: FlagSeverity.BLOCKING,
        label: 'Illegible fax section',
        detail: 'Page 2 of 3 is partially unreadable — insurance member ID could not be extracted.',
      },
      {
        id: 'f2-2',
        severity: FlagSeverity.WARNING,
        label: 'Incomplete insurance information',
        detail: 'Insurance carrier identified (UnitedHealthcare) but member ID is missing.',
      },
    ],
    missingFields: [
      {
        field: 'insuranceMemberId',
        label: 'Insurance Member ID',
        required: true,
        aiOutreachSent: true,
        outreachTimestamp: '2026-04-20T07:01:00Z',
        resolved: false,
      },
      {
        field: 'referringPhone',
        label: 'Referring Provider Phone',
        required: false,
        aiOutreachSent: true,
        outreachTimestamp: '2026-04-20T07:01:00Z',
        resolved: false,
      },
    ],
    decisionFactors: [
      { criterion: 'Geography', requirement: 'Denver service area', patientValue: 'Denver, CO', result: 'PASS' },
      { criterion: 'Insurance', requirement: 'In-network carrier', patientValue: 'UnitedHealthcare', result: 'WARN' },
      { criterion: 'Program Fit', requirement: 'Home Health eligible', patientValue: 'Home Health', result: 'PASS' },
      { criterion: 'Clinical Acuity', requirement: 'Stable for home care', patientValue: 'Post-surgical — stable', result: 'PASS' },
      { criterion: 'Service Area', requirement: 'Active service area', patientValue: 'Denver Metro', result: 'PASS' },
    ],
    capacityInfo: {
      territory: 'Denver',
      available: true,
      availableCount: 2,
      recommendation: 'ACCEPT',
      recommendationLabel: 'Accept — 2 slots available in the Denver service area',
    },
    aiRecommendation: DecisionOutcome.PENDING,
    aiRecommendationReason:
      'Processing is incomplete. Required fields are missing (insurance member ID, referring provider phone). AI outreach has been sent to St. Luke\'s Orthopedic Center. Decision will be re-evaluated when information is received.',
    timeline: [
      {
        id: 'e2-1',
        timestamp: '2026-04-20T06:31:00Z',
        type: 'RECEIVED',
        actor: 'SYSTEM',
        label: 'Referral received via Fax',
        detail: 'Fax received at (800) 555-0300. 3 pages. Uploaded by intake coordinator.',
      },
      {
        id: 'e2-2',
        timestamp: '2026-04-20T06:45:00Z',
        type: 'PROCESSING',
        actor: 'AI',
        label: 'AI extraction started',
        detail: 'Processing fax image — 3 pages',
      },
      {
        id: 'e2-3',
        timestamp: '2026-04-20T06:46:10Z',
        type: 'FLAG',
        actor: 'AI',
        label: 'Flag: Illegible fax section detected',
        detail: 'Page 2 of 3 partially unreadable — insurance section affected',
      },
      {
        id: 'e2-4',
        timestamp: '2026-04-20T06:46:30Z',
        type: 'PROCESSING',
        actor: 'AI',
        label: 'Processing incomplete — missing required fields',
        detail: 'Insurance member ID and referring provider phone could not be extracted',
      },
      {
        id: 'e2-5',
        timestamp: '2026-04-20T07:01:00Z',
        type: 'OUTREACH',
        actor: 'AI',
        label: 'AI-generated outreach to referring provider',
        detail: 'Outreach sent to St. Luke\'s Orthopedic Center requesting insurance member ID and callback number',
      },
      {
        id: 'e2-6',
        timestamp: '2026-04-20T07:45:00Z',
        type: 'PROCESSING',
        actor: 'SYSTEM',
        label: 'Awaiting response from referring provider',
      },
    ],
  },

  // ── Scenario 3: Phone Referral — Manual Entry ─────────────────────────────
  {
    id: 'ref-003',
    scenarioId: 'phone-manual',
    stage: ReferralStage.DECISION,
    status: ReferralStatus.RECOMMENDATION_READY,
    source: ReferralSource.PHONE,
    ingestionType: IngestionType.ASSISTED,
    receivedAt: '2026-04-19T14:22:00Z',
    updatedAt: '2026-04-19T14:58:00Z',
    processingComplete: true,
    patient: {
      name: 'Dorothy Williams',
      dob: '1947-11-04',
      address: '3309 Elm Court',
      city: 'Aurora',
      state: 'CO',
      zip: '80012',
      phone: '(720) 555-0441',
      insurance: 'Medicare',
      insuranceMemberId: 'MED-1YK4-7700',
      diagnosisCodes: ['J44.1', 'Z87.891'],
      diagnosisLabels: ['COPD with acute exacerbation', 'History of nicotine dependence'],
      requestedProgram: 'Home Health',
      referringProvider: 'Dr. Maria Gutierrez',
      referringFacility: 'Aurora Regional Medical Center',
      referringPhone: '(720) 555-0100',
    },
    summary:
      'Dorothy Williams is a 78-year-old female referred for Home Health services following COPD exacerbation. Referral was received via phone call and manually entered into the system by intake coordinator Jamie Reyes. All fields are complete. Insurance is Medicare (verified). Patient resides in Aurora within the active service area. Note: data manually entered — recommend verification at start of care.',
    flags: [
      {
        id: 'f3-1',
        severity: FlagSeverity.INFO,
        label: 'Manually entered referral',
        detail: 'Data entered via phone intake by coordinator Jamie Reyes. Recommend clinical verification at start of care.',
      },
    ],
    missingFields: [],
    decisionFactors: [
      { criterion: 'Geography', requirement: 'Denver/Aurora service area', patientValue: 'Aurora, CO', result: 'PASS' },
      { criterion: 'Insurance', requirement: 'In-network carrier', patientValue: 'Medicare', result: 'PASS' },
      { criterion: 'Program Fit', requirement: 'Home Health eligible', patientValue: 'Home Health', result: 'PASS' },
      { criterion: 'Clinical Acuity', requirement: 'Stable for home care', patientValue: 'Post-exacerbation — stable', result: 'PASS' },
      { criterion: 'Service Area', requirement: 'Active service area', patientValue: 'Aurora / East Denver', result: 'PASS' },
    ],
    capacityInfo: {
      territory: 'Aurora',
      available: true,
      availableCount: 4,
      recommendation: 'ACCEPT',
      recommendationLabel: 'Accept — 4 slots available in the Aurora service area',
    },
    aiRecommendation: DecisionOutcome.ACCEPT,
    aiRecommendationReason:
      'All acceptance criteria are met. Geography, insurance, and program eligibility confirmed. Capacity is available. Manually entered data is flagged for clinical verification at start of care.',
    timeline: [
      {
        id: 'e3-1',
        timestamp: '2026-04-19T14:22:00Z',
        type: 'RECEIVED',
        actor: 'SYSTEM',
        label: 'Referral received via Phone',
        detail: 'Phone call received from Dr. Gutierrez\'s office. Intake coordinator: Jamie Reyes.',
      },
      {
        id: 'e3-2',
        timestamp: '2026-04-19T14:22:00Z',
        type: 'PROCESSING',
        actor: 'Jamie Reyes',
        label: 'Manual intake form completed',
        detail: 'All required fields entered via structured phone intake form',
      },
      {
        id: 'e3-3',
        timestamp: '2026-04-19T14:40:00Z',
        type: 'PROCESSING',
        actor: 'AI',
        label: 'AI validation complete',
        detail: 'All fields present. Manual entry flag added.',
      },
      {
        id: 'e3-4',
        timestamp: '2026-04-19T14:58:00Z',
        type: 'DECISION',
        actor: 'AI',
        label: 'AI recommendation ready: Accept',
        detail: 'All criteria met with manual entry note. Awaiting human confirmation.',
      },
    ],
  },

  // ── Scenario 4: API Referral — Fast Decision ──────────────────────────────
  {
    id: 'ref-004',
    scenarioId: 'api-fast',
    stage: ReferralStage.DECISION,
    status: ReferralStatus.RECOMMENDATION_READY,
    source: ReferralSource.API,
    ingestionType: IngestionType.AUTO,
    receivedAt: '2026-04-20T09:02:00Z',
    updatedAt: '2026-04-20T09:02:47Z',
    processingComplete: true,
    patient: {
      name: 'James Liu',
      dob: '1961-05-18',
      address: '7701 Pine Ridge Road',
      city: 'Denver',
      state: 'CO',
      zip: '80221',
      phone: '(303) 555-0788',
      insurance: 'Cigna',
      insuranceMemberId: 'CIG-44-991-2205',
      diagnosisCodes: ['I63.9', 'G81.90'],
      diagnosisLabels: ['Cerebral infarction, unspecified', 'Hemiplegia, unspecified'],
      requestedProgram: 'Home Health',
      referringProvider: 'Dr. Priya Nair',
      referringFacility: 'Colorado Neurological Institute',
      referringPhone: '(303) 555-0900',
      referringFax: '(303) 555-0901',
    },
    summary:
      'James Liu is a 64-year-old male referred for Home Health following ischemic stroke with residual hemiplegia. Referral received via EHR API integration with Colorado Neurological Institute. All data is pre-structured and complete. Insurance verified as Cigna (in-network). Rapid processing complete in under 1 minute.',
    flags: [],
    missingFields: [],
    decisionFactors: [
      { criterion: 'Geography', requirement: 'Denver service area', patientValue: 'Denver, CO', result: 'PASS' },
      { criterion: 'Insurance', requirement: 'In-network carrier', patientValue: 'Cigna', result: 'PASS' },
      { criterion: 'Program Fit', requirement: 'Home Health eligible', patientValue: 'Home Health', result: 'PASS' },
      { criterion: 'Clinical Acuity', requirement: 'Stable for home care', patientValue: 'Stable — post-acute stroke', result: 'PASS' },
      { criterion: 'Service Area', requirement: 'Active service area', patientValue: 'Denver Metro', result: 'PASS' },
    ],
    capacityInfo: {
      territory: 'Denver',
      available: true,
      availableCount: 3,
      recommendation: 'ACCEPT',
      recommendationLabel: 'Accept — 3 slots available in the Denver service area',
    },
    aiRecommendation: DecisionOutcome.ACCEPT,
    aiRecommendationReason:
      'Pre-structured API referral — all fields validated automatically. All criteria met. Fastest path to decision. Ready for immediate acceptance.',
    timeline: [
      {
        id: 'e4-1',
        timestamp: '2026-04-20T09:02:00Z',
        type: 'RECEIVED',
        actor: 'SYSTEM',
        label: 'Referral received via API',
        detail: 'Received from Colorado Neurological Institute EHR integration (endpoint: /api/referrals/inbound)',
      },
      {
        id: 'e4-2',
        timestamp: '2026-04-20T09:02:03Z',
        type: 'PROCESSING',
        actor: 'AI',
        label: 'Auto-validation started — pre-structured payload',
      },
      {
        id: 'e4-3',
        timestamp: '2026-04-20T09:02:28Z',
        type: 'PROCESSING',
        actor: 'AI',
        label: 'Validation complete — all fields present',
        detail: 'No missing data. No flags raised.',
      },
      {
        id: 'e4-4',
        timestamp: '2026-04-20T09:02:47Z',
        type: 'DECISION',
        actor: 'AI',
        label: 'AI recommendation ready: Accept',
        detail: 'Total processing time: 47 seconds.',
      },
    ],
  },

  // ── Scenario 5: Capacity Constraint ──────────────────────────────────────
  {
    id: 'ref-005',
    scenarioId: 'capacity-hold',
    stage: ReferralStage.DECISION,
    status: ReferralStatus.RECOMMENDATION_READY,
    source: ReferralSource.EMAIL,
    ingestionType: IngestionType.AUTO,
    receivedAt: '2026-04-20T07:55:00Z',
    updatedAt: '2026-04-20T08:02:00Z',
    processingComplete: true,
    patient: {
      name: 'Helen Park',
      dob: '1955-09-30',
      address: '619 Spruce Lane',
      city: 'Boulder',
      state: 'CO',
      zip: '80302',
      phone: '(720) 555-0312',
      insurance: 'Humana',
      insuranceMemberId: 'HUM-7734-0022',
      diagnosisCodes: ['C50.911', 'Z85.3'],
      diagnosisLabels: ['Malignant neoplasm of breast', 'Personal history of malignant neoplasm'],
      requestedProgram: 'Home Health',
      referringProvider: 'Dr. Kevin Strand',
      referringFacility: 'Boulder Cancer Care Center',
      referringPhone: '(720) 555-0200',
    },
    summary:
      'Helen Park is a 70-year-old female referred for Home Health services following breast cancer treatment. All clinical and insurance criteria are met. However, the Boulder service area is currently at capacity with 0 available slots. AI recommends holding this referral until capacity becomes available, with an estimated opening in 3–5 business days.',
    flags: [
      {
        id: 'f5-1',
        severity: FlagSeverity.WARNING,
        label: 'Capacity hold — Boulder service area full',
        detail: 'No available provider slots in Boulder service area as of today. Recommend delayed start.',
      },
    ],
    missingFields: [],
    decisionFactors: [
      { criterion: 'Geography', requirement: 'Boulder service area', patientValue: 'Boulder, CO', result: 'PASS' },
      { criterion: 'Insurance', requirement: 'In-network carrier', patientValue: 'Humana', result: 'PASS' },
      { criterion: 'Program Fit', requirement: 'Home Health eligible', patientValue: 'Home Health', result: 'PASS' },
      { criterion: 'Clinical Acuity', requirement: 'Stable for home care', patientValue: 'Stable — post-treatment', result: 'PASS' },
      { criterion: 'Service Area', requirement: 'Active service area', patientValue: 'Boulder', result: 'PASS' },
    ],
    capacityInfo: {
      territory: 'Boulder',
      available: false,
      availableCount: 0,
      recommendation: 'HOLD',
      recommendationLabel: 'Hold — no capacity in Boulder service area',
    },
    aiRecommendation: DecisionOutcome.PENDING,
    aiRecommendationReason:
      'Clinical and insurance criteria are all met. However, the Boulder service area has no available capacity. Recommend holding this referral. Estimated availability: 3–5 business days. Consider delayed start authorization.',
    timeline: [
      {
        id: 'e5-1',
        timestamp: '2026-04-20T07:55:00Z',
        type: 'RECEIVED',
        actor: 'SYSTEM',
        label: 'Referral received via Email',
        detail: 'Auto-ingested from referrals@medarrive.com inbox',
      },
      {
        id: 'e5-2',
        timestamp: '2026-04-20T07:55:10Z',
        type: 'PROCESSING',
        actor: 'AI',
        label: 'Extraction and validation complete',
        detail: 'All fields present. No missing information.',
      },
      {
        id: 'e5-3',
        timestamp: '2026-04-20T08:00:00Z',
        type: 'PROCESSING',
        actor: 'AI',
        label: 'Capacity check: Boulder service area',
        detail: 'Result: 0 available slots. Territory at capacity.',
      },
      {
        id: 'e5-4',
        timestamp: '2026-04-20T08:02:00Z',
        type: 'DECISION',
        actor: 'AI',
        label: 'AI recommendation: Hold — no capacity',
        detail: 'All clinical criteria met, but capacity constraint prevents acceptance. Awaiting human review.',
      },
    ],
    priorEncounters: [
      {
        id: 'pe-005-1',
        date: '2025-08-14',
        program: 'Home Health',
        outcome: 'DECLINED',
        declineReason: 'Insurance not in-network — Cigna Basic PPO is not a contracted carrier.',
        summary: 'Referral for Home Health following initial breast cancer treatment. Declined at decision stage — insurance carrier (Cigna Basic PPO) was not in-network and no exception authorization was obtained. Patient was referred to an alternative provider.',
      },
    ],
  },

  // ── Scenario 6: Override Case ─────────────────────────────────────────────
  {
    id: 'ref-006',
    scenarioId: 'override-accept',
    stage: ReferralStage.EXECUTION,
    status: ReferralStatus.ACCEPTED,
    source: ReferralSource.FAX,
    ingestionType: IngestionType.ASSISTED,
    receivedAt: '2026-04-19T11:05:00Z',
    updatedAt: '2026-04-19T15:30:00Z',
    processingComplete: true,
    finalDecision: DecisionOutcome.OVERRIDE_ACCEPT,
    finalDecisionBy: 'Sarah Brennan',
    finalDecisionAt: '2026-04-19T15:30:00Z',
    overrideReason:
      'Family has confirmed self-pay authorization with signed financial agreement on file. Clinical team at St. Joseph\'s has approved exception. Patient has no other care options in the area.',
    patient: {
      name: 'Frank Nguyen',
      dob: '1952-01-15',
      address: '2204 Oak Blvd',
      city: 'Denver',
      state: 'CO',
      zip: '80205',
      phone: '(303) 555-0667',
      insurance: 'Out-of-Network PPO',
      insuranceMemberId: 'OON-332-114',
      diagnosisCodes: ['N18.3', 'I12.9'],
      diagnosisLabels: ['Chronic kidney disease, stage 3', 'Hypertensive CKD'],
      requestedProgram: 'Home Health',
      referringProvider: 'Dr. Patricia Solis',
      referringFacility: 'St. Joseph\'s Renal Center',
      referringPhone: '(303) 555-0800',
      referringFax: '(303) 555-0801',
    },
    summary:
      'Frank Nguyen is a 74-year-old male referred for Home Health services for chronic kidney disease management. AI recommended decline due to out-of-network insurance. Care coordinator Sarah Brennan overrode the decision based on confirmed self-pay authorization and clinical exception approval from St. Joseph\'s. Execution is in progress.',
    flags: [
      {
        id: 'f6-1',
        severity: FlagSeverity.WARNING,
        label: 'Out-of-network insurance',
        detail: 'Insurance plan (Out-of-Network PPO) is not in the standard in-network carrier list.',
      },
    ],
    missingFields: [],
    decisionFactors: [
      { criterion: 'Geography', requirement: 'Denver service area', patientValue: 'Denver, CO', result: 'PASS' },
      { criterion: 'Insurance', requirement: 'In-network carrier', patientValue: 'Out-of-Network PPO', result: 'FAIL' },
      { criterion: 'Program Fit', requirement: 'Home Health eligible', patientValue: 'Home Health', result: 'PASS' },
      { criterion: 'Clinical Acuity', requirement: 'Stable for home care', patientValue: 'Stable — CKD stage 3', result: 'PASS' },
      { criterion: 'Service Area', requirement: 'Active service area', patientValue: 'Denver Metro', result: 'PASS' },
    ],
    capacityInfo: {
      territory: 'Denver',
      available: true,
      availableCount: 2,
      recommendation: 'ACCEPT',
      recommendationLabel: 'Accept — 2 slots available in the Denver service area',
    },
    aiRecommendation: DecisionOutcome.DECLINE,
    aiRecommendationReason:
      'Insurance carrier is out-of-network. Standard acceptance criteria not met for insurance. Recommend decline unless self-pay or exception authorization obtained.',
    executionSteps: [
      { label: 'EHR Patient Record Created', status: 'COMPLETE', timestamp: '2026-04-19T15:32:00Z' },
      { label: 'Episode of Care Created', status: 'COMPLETE', timestamp: '2026-04-19T15:33:10Z' },
      { label: 'Referral Sent to Operate for Intake Assignment', status: 'COMPLETE', timestamp: '2026-04-19T15:34:00Z' },
      { label: 'Visit(s) Scheduled', status: 'IN_PROGRESS' },
    ],
    timeline: [
      {
        id: 'e6-1',
        timestamp: '2026-04-19T11:05:00Z',
        type: 'RECEIVED',
        actor: 'SYSTEM',
        label: 'Referral received via Fax',
        detail: 'Fax received from St. Joseph\'s Renal Center. 2 pages.',
      },
      {
        id: 'e6-2',
        timestamp: '2026-04-19T11:20:00Z',
        type: 'PROCESSING',
        actor: 'AI',
        label: 'Processing complete',
        detail: 'All fields extracted. Insurance flagged as out-of-network.',
      },
      {
        id: 'e6-3',
        timestamp: '2026-04-19T11:25:00Z',
        type: 'DECISION',
        actor: 'AI',
        label: 'AI recommendation: Decline',
        detail: 'Out-of-network insurance. Standard criteria not met.',
      },
      {
        id: 'e6-4',
        timestamp: '2026-04-19T15:30:00Z',
        type: 'OVERRIDE',
        actor: 'Sarah Brennan',
        label: 'Human override: Accept',
        detail: 'Override reason: self-pay authorization confirmed, clinical exception approved by referring facility.',
      },
      {
        id: 'e6-5',
        timestamp: '2026-04-19T15:32:00Z',
        type: 'EXECUTION',
        actor: 'SYSTEM',
        label: 'Execution started — EHR and Operate',
      },
    ],
  },

  // ── Scenario 7: Auto-Decline — Out of Territory (outreach already sent) ─────
  {
    id: 'ref-007',
    scenarioId: 'auto-decline-sent',
    stage: ReferralStage.EXECUTION,
    status: ReferralStatus.AUTO_DECLINED,
    source: ReferralSource.EMAIL,
    ingestionType: IngestionType.AUTO,
    receivedAt: '2026-04-19T16:40:00Z',
    updatedAt: '2026-04-19T17:15:00Z',
    processingComplete: false,
    autoDeclined: true,
    autoDeclineReason: 'Patient location (Pueblo, CO) is outside all active service areas.',
    outreachStatus: 'SENT',
    outreachScheduledAt: '2026-04-19T20:40:00Z',
    outreachSentAt: '2026-04-19T20:40:00Z',
    finalDecision: DecisionOutcome.DECLINE,
    finalDecisionBy: 'System (Auto)',
    finalDecisionAt: '2026-04-19T16:41:00Z',
    patient: {
      name: 'Patricia Adams',
      dob: '1943-06-22',
      address: '8800 Mountain View Rd',
      city: 'Pueblo',
      state: 'CO',
      zip: '81003',
      phone: '(719) 555-0188',
      insurance: 'Aetna',
      insuranceMemberId: 'AET-991-003321',
      diagnosisCodes: ['M81.0', 'Z79.01'],
      diagnosisLabels: ['Age-related osteoporosis', 'Long-term use of anticoagulants'],
      requestedProgram: 'Home Health',
      referringProvider: 'Dr. Thomas Hale',
      referringFacility: 'Pueblo Community Hospital',
      referringPhone: '(719) 555-0400',
    },
    summary:
      'Patricia Adams is an 82-year-old female referred for Home Health services from Pueblo, CO. Despite meeting insurance and clinical criteria, the patient address falls outside all active service areas. System automatically declined and outreach notification has been sent to the referring provider.',
    flags: [
      {
        id: 'f7-1',
        severity: FlagSeverity.BLOCKING,
        label: 'Outside active service area',
        detail: 'Pueblo, CO is not within any active service area.',
      },
    ],
    missingFields: [],
    decisionFactors: [
      { criterion: 'Geography', requirement: 'Active service area', patientValue: 'Pueblo, CO', result: 'FAIL' },
      { criterion: 'Insurance', requirement: 'In-network carrier', patientValue: 'Aetna', result: 'PASS' },
      { criterion: 'Program Fit', requirement: 'Home Health eligible', patientValue: 'Home Health', result: 'PASS' },
      { criterion: 'Clinical Acuity', requirement: 'Stable for home care', patientValue: 'Stable', result: 'PASS' },
      { criterion: 'Service Area', requirement: 'Active service area', patientValue: 'Pueblo — not active', result: 'FAIL' },
    ],
    capacityInfo: {
      territory: 'Pueblo',
      available: false,
      availableCount: 0,
      recommendation: 'HOLD',
      recommendationLabel: 'N/A — outside service area',
    },
    aiRecommendation: DecisionOutcome.DECLINE,
    aiRecommendationReason:
      'Patient is located in Pueblo, CO which is outside all active MedArrive service territories. Cannot accept referral. Recommend decline with referral to alternative provider.',
    executionSteps: [
      { label: 'Decline Notification Drafted', status: 'COMPLETE', timestamp: '2026-04-19T17:11:00Z' },
      { label: 'Outbound Communication Sent', status: 'COMPLETE', timestamp: '2026-04-19T17:15:00Z' },
    ],
    timeline: [
      {
        id: 'e7-1',
        timestamp: '2026-04-19T16:40:00Z',
        type: 'RECEIVED',
        actor: 'SYSTEM',
        label: 'Referral received via Email',
        detail: 'Auto-ingested from referrals@medarrive.com inbox',
      },
      {
        id: 'e7-2',
        timestamp: '2026-04-19T16:41:00Z',
        type: 'PROCESSING',
        actor: 'AI',
        label: 'Processing complete — geography flag raised',
        detail: 'Pueblo, CO is not within any active territory.',
      },
      {
        id: 'e7-3',
        timestamp: '2026-04-19T16:42:00Z',
        type: 'DECISION',
        actor: 'AI',
        label: 'AI recommendation: Decline',
        detail: 'Outside active service area.',
      },
      {
        id: 'e7-4',
        timestamp: '2026-04-19T16:41:00Z',
        type: 'DECISION',
        actor: 'SYSTEM',
        label: 'Auto-declined — outside service area',
        detail: 'Pueblo, CO does not match any active service territory. Decline notification scheduled.',
      },
      {
        id: 'e7-5',
        timestamp: '2026-04-19T20:40:00Z',
        type: 'EXECUTION',
        actor: 'SYSTEM',
        label: 'Decline outreach sent',
        detail: 'Decline notification sent to Dr. Thomas Hale at Pueblo Community Hospital.',
      },
    ],
  },

  // ── Scenario 7b: Coordinator-Confirmed Decline ───────────────────────────────
  {
    id: 'ref-009',
    scenarioId: 'manual-decline',
    stage: ReferralStage.EXECUTION,
    status: ReferralStatus.DECLINED,
    source: ReferralSource.EMAIL,
    ingestionType: IngestionType.AUTO,
    receivedAt: '2026-04-18T09:10:00Z',
    updatedAt: '2026-04-18T11:44:00Z',
    processingComplete: true,
    finalDecision: DecisionOutcome.DECLINE,
    finalDecisionBy: 'Sarah Brennan',
    finalDecisionAt: '2026-04-18T11:44:00Z',
    overrideReason: 'Colorado Springs is not within any active service area. No alternative provider referral available at this time.',
    patient: {
      name: 'Carol Simmons',
      dob: '1950-08-17',
      address: '722 Gold Camp Road',
      city: 'Colorado Springs',
      state: 'CO',
      zip: '80906',
      phone: '(719) 555-0244',
      insurance: 'Humana',
      insuranceMemberId: 'HUM-4421-8800',
      diagnosisCodes: ['G35', 'Z87.39'],
      diagnosisLabels: ['Multiple sclerosis', 'Personal history of other musculoskeletal disorders'],
      requestedProgram: 'Home Health',
      referringProvider: 'Dr. Linda Marsh',
      referringFacility: 'Colorado Springs Neurology Associates',
      referringPhone: '(719) 555-0500',
      referringFax: '(719) 555-0501',
    },
    summary:
      'Carol Simmons is a 75-year-old female referred for Home Health services for multiple sclerosis management. All clinical and insurance criteria are met. However, the patient is located in Colorado Springs, CO, which is not currently within any active service area. AI recommended decline; coordinator Sarah Brennan reviewed and confirmed. Outbound decline notification has been sent to the referring provider.',
    flags: [
      {
        id: 'f9-1',
        severity: FlagSeverity.BLOCKING,
        label: 'Outside active service area',
        detail: 'Colorado Springs, CO is not within any currently active service area.',
      },
    ],
    missingFields: [],
    decisionFactors: [
      { criterion: 'Geography', requirement: 'Active service area', patientValue: 'Colorado Springs, CO', result: 'FAIL' },
      { criterion: 'Insurance', requirement: 'In-network carrier', patientValue: 'Humana', result: 'PASS' },
      { criterion: 'Program Fit', requirement: 'Home Health eligible', patientValue: 'Home Health', result: 'PASS' },
      { criterion: 'Clinical Acuity', requirement: 'Stable for home care', patientValue: 'Stable — MS managed', result: 'PASS' },
      { criterion: 'Service Area', requirement: 'Active service area', patientValue: 'Colorado Springs — not active', result: 'FAIL' },
    ],
    capacityInfo: {
      territory: 'Colorado Springs',
      available: false,
      availableCount: 0,
      recommendation: 'HOLD',
      recommendationLabel: 'N/A — outside service area',
    },
    aiRecommendation: DecisionOutcome.DECLINE,
    aiRecommendationReason:
      'Patient is located in Colorado Springs, CO, which is outside all active service areas. All other criteria pass. Recommend decline and referral to an alternative provider.',
    executionSteps: [
      { label: 'Decline Notification Drafted', status: 'COMPLETE', timestamp: '2026-04-18T11:45:00Z' },
      { label: 'Outbound Communication Sent', status: 'COMPLETE', timestamp: '2026-04-18T11:46:00Z' },
    ],
    timeline: [
      {
        id: 'e9-1',
        timestamp: '2026-04-18T09:10:00Z',
        type: 'RECEIVED',
        actor: 'SYSTEM',
        label: 'Referral received via Email',
        detail: 'Auto-ingested from referrals@medarrive.com inbox',
      },
      {
        id: 'e9-2',
        timestamp: '2026-04-18T09:10:22Z',
        type: 'PROCESSING',
        actor: 'AI',
        label: 'AI extraction and validation complete',
        detail: 'All fields present. Geography flag raised — Colorado Springs is outside active service area.',
      },
      {
        id: 'e9-3',
        timestamp: '2026-04-18T09:11:00Z',
        type: 'DECISION',
        actor: 'AI',
        label: 'AI recommendation: Decline',
        detail: 'Outside active service area. All other criteria pass.',
      },
      {
        id: 'e9-4',
        timestamp: '2026-04-18T11:44:00Z',
        type: 'DECISION',
        actor: 'Sarah Brennan',
        label: 'Decision: Declined',
        detail: 'Colorado Springs is not within any active service area. No alternative provider referral available at this time.',
      },
      {
        id: 'e9-5',
        timestamp: '2026-04-18T11:45:00Z',
        type: 'EXECUTION',
        actor: 'SYSTEM',
        label: 'Decline notification drafted',
      },
      {
        id: 'e9-6',
        timestamp: '2026-04-18T11:46:00Z',
        type: 'EXECUTION',
        actor: 'SYSTEM',
        label: 'Outbound communication sent',
        detail: 'Decline notice sent to Dr. Linda Marsh at Colorado Springs Neurology Associates.',
      },
    ],
  },

  // ── Scenario 10: Post-Surgical Wound Care — Additional Documents Expected ───
  {
    id: 'ref-010',
    scenarioId: 'post-surgical-wound',
    stage: ReferralStage.DECISION,
    status: ReferralStatus.RECOMMENDATION_READY,
    source: ReferralSource.EFAX,
    ingestionType: IngestionType.AUTO,
    receivedAt: '2026-04-22T10:30:00Z',
    updatedAt: '2026-04-22T10:45:00Z',
    processingComplete: true,
    patient: {
      name: 'James Whitfield',
      dob: '1958-01-14',
      address: '2901 Vine Street',
      city: 'Denver',
      state: 'CO',
      zip: '80205',
      phone: '(303) 555-0147',
      insurance: 'Medicare',
      insuranceMemberId: 'MCR-1EG4-TE5',
      diagnosisCodes: ['Z48.89', 'M17.11'],
      diagnosisLabels: ['Post-operative wound care', 'Primary osteoarthritis, right knee'],
      requestedProgram: 'Home Health',
      referringProvider: 'Dr. Rachel Kim',
      referringFacility: 'Denver Orthopedic & Sports Medicine Center',
      referringPhone: '(303) 555-0310',
      referringFax: '(303) 555-0311',
    },
    summary:
      'James Whitfield is a 68-year-old male referred for Home Health post-operative wound care following right knee arthroplasty at Denver Orthopedic & Sports Medicine Center. He resides in Denver, CO within the active service area. Insurance is Medicare (in-network). Initial referral order received via e-fax. Referring provider has indicated that clinical notes and a formal wound assessment will follow separately. AI processing is complete based on available documentation — all intake criteria are met.',
    flags: [
      {
        id: 'f10-1',
        severity: FlagSeverity.INFO,
        label: 'Additional documentation expected',
        detail: 'Referring provider noted that clinical progress notes and a wound assessment will be sent separately after discharge. Referral can proceed on current documentation.',
      },
    ],
    missingFields: [],
    decisionFactors: [
      { criterion: 'Geography', requirement: 'Denver service area', patientValue: 'Denver, CO', result: 'PASS' },
      { criterion: 'Insurance', requirement: 'In-network carrier', patientValue: 'Medicare', result: 'PASS' },
      { criterion: 'Program Fit', requirement: 'Home Health eligible', patientValue: 'Post-surgical wound care', result: 'PASS' },
      { criterion: 'Clinical Acuity', requirement: 'Stable for home care', patientValue: 'Stable — post-op day 3', result: 'PASS' },
      { criterion: 'Service Area', requirement: 'Active service area', patientValue: 'Denver Metro', result: 'PASS' },
    ],
    capacityInfo: {
      territory: 'Denver',
      available: true,
      availableCount: 2,
      recommendation: 'ACCEPT',
      recommendationLabel: 'Accept — 2 slots available in the Denver service area',
    },
    aiRecommendation: DecisionOutcome.ACCEPT,
    aiRecommendationReason:
      'All acceptance criteria are met based on available documentation. Geography, insurance, and program fit confirmed. Additional clinical documentation is expected per the referring provider and will be matched to this referral when received. Recommend proceeding.',
    timeline: [
      {
        id: 'e10-1',
        timestamp: '2026-04-22T10:30:00Z',
        type: 'RECEIVED',
        actor: 'SYSTEM',
        label: 'Referral received via E-Fax',
        detail: 'Referral order received from Denver Orthopedic & Sports Medicine Center.',
      },
      {
        id: 'e10-2',
        timestamp: '2026-04-22T10:32:00Z',
        type: 'PROCESSING',
        actor: 'AI',
        label: 'AI extraction started',
        detail: 'Auto-ingested — extracting and validating fields from e-fax document.',
      },
      {
        id: 'e10-3',
        timestamp: '2026-04-22T10:44:00Z',
        type: 'PROCESSING',
        actor: 'AI',
        label: 'Processing complete',
        detail: 'All required fields extracted. Referring provider noted additional clinical documentation to follow.',
      },
      {
        id: 'e10-4',
        timestamp: '2026-04-22T10:45:00Z',
        type: 'DECISION',
        actor: 'AI',
        label: 'AI recommendation ready: Accept',
        detail: 'All intake criteria met. Additional documents expected — referral can proceed on current documentation.',
      },
    ],
  },

  // ── Scenario 8: Auto-Decline — Out of Territory (outreach pending) ──────────
  {
    id: 'ref-008',
    scenarioId: 'auto-decline-pending',
    stage: ReferralStage.EXECUTION,
    status: ReferralStatus.AUTO_DECLINED,
    source: ReferralSource.EFAX,
    ingestionType: IngestionType.AUTO,
    receivedAt: '2026-04-21T07:52:00Z',
    updatedAt: '2026-04-21T07:52:00Z',
    processingComplete: false,
    autoDeclined: true,
    autoDeclineReason: 'Patient location (Phoenix, AZ) is outside all active service areas.',
    outreachStatus: 'PENDING',
    outreachScheduledAt: '2026-04-21T11:52:00Z',
    finalDecision: DecisionOutcome.DECLINE,
    finalDecisionBy: 'System (Auto)',
    finalDecisionAt: '2026-04-21T07:52:00Z',
    patient: {
      name: 'Robert Nguyen',
      dob: '1958-03-07',
      address: '4820 N 32nd Street',
      city: 'Phoenix',
      state: 'AZ',
      zip: '85018',
      phone: '(602) 555-0176',
      insurance: 'UnitedHealthcare',
      insuranceMemberId: 'UHC-773-88210',
      diagnosisCodes: ['I63.9'],
      diagnosisLabels: ['Cerebral infarction, unspecified'],
      requestedProgram: 'Home Health',
      referringProvider: 'Dr. Angela Torres',
      referringFacility: 'Banner University Medical Center',
      referringPhone: '(602) 555-0300',
      referringFax: '(602) 555-0301',
    },
    summary:
      'Robert Nguyen is a 67-year-old male referred for Home Health following cerebral infarction. Insurance and clinical criteria appear to be met, however the patient is located in Phoenix, AZ which is not within any active service area. Referral was automatically declined. Outreach notification to the referring provider is pending.',
    flags: [
      {
        id: 'f8-1',
        severity: FlagSeverity.BLOCKING,
        label: 'Outside active service area',
        detail: 'Phoenix, AZ is not within any currently active service territory.',
      },
    ],
    missingFields: [],
    decisionFactors: [
      { criterion: 'Geography', requirement: 'Active service area', patientValue: 'Phoenix, AZ', result: 'FAIL' },
      { criterion: 'Insurance', requirement: 'In-network carrier', patientValue: 'UnitedHealthcare', result: 'PASS' },
      { criterion: 'Program Fit', requirement: 'Home Health eligible', patientValue: 'Home Health', result: 'PASS' },
    ],
    capacityInfo: {
      territory: 'Phoenix',
      available: false,
      availableCount: 0,
      recommendation: 'HOLD',
      recommendationLabel: 'N/A — outside service area',
    },
    aiRecommendation: DecisionOutcome.DECLINE,
    aiRecommendationReason:
      'Phoenix, AZ is not within any active service area. Referral automatically declined.',
    timeline: [
      {
        id: 'e8-1',
        timestamp: '2026-04-21T07:52:00Z',
        type: 'RECEIVED',
        actor: 'SYSTEM',
        label: 'Referral received via E-Fax',
        detail: 'Auto-ingested from e-fax system.',
      },
      {
        id: 'e8-2',
        timestamp: '2026-04-21T07:52:00Z',
        type: 'DECISION',
        actor: 'SYSTEM',
        label: 'Auto-declined — outside service area',
        detail: 'Phoenix, AZ does not match any active service territory. Decline notification scheduled.',
      },
    ],
  },
];

// ==================== TERRITORY CHECK ====================

// In prod: replace with a DB-backed territory table keyed by org and checked against
// geocoded patient address. Also apply business-hours logic when scheduling outreach.
const SUPPORTED_METROS = new Set([
  'denver', 'aurora', 'boulder', 'lakewood', 'englewood', 'littleton', 'westminster', 'broomfield',
  'seattle', 'bellevue', 'tacoma', 'redmond', 'kirkland', 'renton',
]);

function isInServiceTerritory(city: string): boolean {
  return SUPPORTED_METROS.has(city.toLowerCase().trim());
}

// In prod: compute based on org business hours + timezone (e.g. next business day 9am)
function scheduleOutreachAt(from: Date): string {
  return new Date(from.getTime() + 4 * 60 * 60 * 1000).toISOString();
}

// ==================== SIMULATE INTAKE HELPER ====================

let simulatedCounter = 100;

function buildSimulatedReferral(source: ReferralSource): Referral {
  simulatedCounter += 1;
  const id = `ref-sim-${simulatedCounter}`;
  const now = new Date().toISOString();

  const names: Record<ReferralSource, string> = {
    [ReferralSource.EMAIL]: 'Carol Bennett',
    [ReferralSource.EFAX]: 'Linda Hoffman',
    [ReferralSource.FAX]: 'Raymond Ortiz',
    [ReferralSource.PHONE]: 'Susan Patel',
    [ReferralSource.API]: 'Marcus Webb',
  };

  const ingestionType =
    source === ReferralSource.EMAIL || source === ReferralSource.EFAX || source === ReferralSource.API
      ? IngestionType.AUTO
      : IngestionType.ASSISTED;

  const sourceLabel: Record<ReferralSource, string> = {
    [ReferralSource.EMAIL]: 'Email',
    [ReferralSource.EFAX]: 'E-Fax',
    [ReferralSource.FAX]: 'Fax',
    [ReferralSource.PHONE]: 'Phone',
    [ReferralSource.API]: 'API',
  };

  return {
    id,
    stage: ReferralStage.INTAKE,
    status: ReferralStatus.RECEIVED,
    source,
    ingestionType,
    receivedAt: now,
    updatedAt: now,
    processingComplete: false,
    patient: {
      name: names[source],
      dob: '1958-04-10',
      address: '100 Demo Street',
      city: 'Denver',
      state: 'CO',
      zip: '80201',
      insurance: 'Aetna',
      diagnosisCodes: ['I50.9'],
      diagnosisLabels: ['Congestive Heart Failure'],
      requestedProgram: 'Home Health',
      referringProvider: 'Dr. Demo Provider',
      referringFacility: 'Demo Hospital',
    },
    summary: `Simulated referral received via ${sourceLabel[source]}. Processing has not yet started.`,
    flags: [],
    missingFields: [],
    decisionFactors: [],
    capacityInfo: {
      territory: 'Denver',
      available: true,
      availableCount: 2,
      recommendation: 'ACCEPT',
      recommendationLabel: 'Accept — 2 slots available in the Denver service area',
    },
    aiRecommendation: DecisionOutcome.PENDING,
    aiRecommendationReason: 'Awaiting processing.',
    timeline: [
      {
        id: `${id}-t1`,
        timestamp: now,
        type: 'RECEIVED',
        actor: 'SYSTEM',
        label: `Referral received via ${sourceLabel[source]}`,
        detail: ingestionType === IngestionType.AUTO ? 'Auto-ingested' : 'Manually assisted entry',
      },
    ],
  };
}

// ==================== INCOMING DOCUMENT POOL ====================

const INCOMING_DOC_TEMPLATES: Array<{
  type: ReferralDocumentType;
  fileName: string;
  source: ReferralDocument['source'];
  matchConfidence: 'HIGH' | 'MEDIUM';
  matchedOn: string[];
  buildContent: (r: Referral) => string;
}> = [
  {
    type: 'Clinical Notes',
    fileName: 'clinical_notes_progress.pdf',
    source: 'FAX',
    matchConfidence: 'HIGH',
    matchedOn: ['Patient name', 'Date of birth', 'Referring facility'],
    buildContent: (r) =>
      `Clinical Progress Notes\n\nPatient: ${r.patient.name}\nDOB: ${r.patient.dob}\n\nDiagnosis: ${r.patient.diagnosisLabels.join(', ')}\n\nClinical Assessment:\nPatient is medically stable and appropriate for home health services. Vital signs within normal limits. Ambulation requires minimal assistance. Wound site clean, no signs of infection.\n\nHome Health Orders:\n- Skilled nursing 3×/week × 4 weeks\n- Physical therapy evaluation\n- Patient education on medication management\n\nSigned: ${r.patient.referringProvider}\n${r.patient.referringFacility}`,
  },
  {
    type: 'Wound Assessment',
    fileName: 'wound_assessment_form.pdf',
    source: 'EFAX',
    matchConfidence: 'HIGH',
    matchedOn: ['Patient name', 'Date of birth'],
    buildContent: (r) =>
      `Wound Assessment Form\n\nPatient: ${r.patient.name}\nDOB: ${r.patient.dob}\n\nWound Type: Post-surgical incision\nLocation: Lower right extremity\nSize: 4.2 cm × 1.8 cm\nDepth: Full thickness with tunneling (1.5 cm)\nExudate: Moderate purulent\nSurrounding skin: Erythema present, 2 cm periwound\nOdor: Present\nCulture result: MRSA positive (pending repeat culture)\n\nDressing: Antimicrobial foam with silver\nFrequency: Daily change, skilled nursing required\n\n⚠️ Clinical Note: Wound complexity exceeds initial referral documentation. Condition requires complex wound care management. Standard home health wound care protocols may be insufficient — recommend clinical review before proceeding.\n\nAssessed by: ${r.patient.referringProvider}\n${r.patient.referringFacility}`,
  },
  {
    type: 'Discharge Summary',
    fileName: 'discharge_summary.pdf',
    source: 'EFAX',
    matchConfidence: 'MEDIUM',
    matchedOn: ['Patient name', 'Referring facility'],
    buildContent: (r) =>
      `Discharge Summary\n\nPatient: ${r.patient.name}\nDOB: ${r.patient.dob}\nAdmit Date: ${new Date(new Date(r.receivedAt).getTime() - 3 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}\nDischarge Date: ${new Date(r.receivedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}\n\nPrimary Diagnosis: ${r.patient.diagnosisLabels[0] ?? 'See clinical records'}\nSecondary Diagnoses: ${r.patient.diagnosisLabels.slice(1).join(', ') || 'None documented'}\n\nHospital Course:\nPatient was admitted for management of ${r.patient.diagnosisLabels[0] ?? 'primary condition'}. Treatment course completed without complications. Medically stable at discharge.\n\nDischarge Disposition: Home (family member's residence)\nDischarge Address: 5220 Mesa Dr, Pueblo, CO 81003\n  ⚠️ Note: Patient will recover at daughter's home — differs from patient's registered address on referral (${r.patient.address}, ${r.patient.city}, ${r.patient.state} ${r.patient.zip}). Confirm final location with referring provider before proceeding.\n\nFollowing Provider: ${r.patient.referringProvider}\nFacility: ${r.patient.referringFacility}\n\nMedications at Discharge: See medication reconciliation list (attached)`,
  },
];

// ==================== PROVIDER ====================

export type TransitionNotifyFn = (
  referralId: string,
  params: { type: 'info' | 'warning'; title: string; message: string; showToast?: boolean }
) => void;

export type TransitionReferralAcceptedFn = (referralId: string, patient: ReferralPatient) => void;

interface TransitionProviderProps {
  children: ReactNode;
  onNotify?: TransitionNotifyFn;
  onReferralAccepted?: TransitionReferralAcceptedFn;
}

export const TransitionProvider: React.FC<TransitionProviderProps> = ({ children, onNotify, onReferralAccepted }) => {
  const [referrals, setReferrals] = useState<Referral[]>(MOCK_REFERRALS);
  const [unseenAttentionIds, setUnseenAttentionIds] = useState<Set<string>>(new Set());
  const [currentDemoUser, setCurrentDemoUser] = useState<TransitionDemoUser>(TRANSITION_DEMO_USERS[0]);
  const switchDemoUser = useCallback((user: TransitionDemoUser) => setCurrentDemoUser(user), []);

  const onNotifyRef = useRef(onNotify);
  useEffect(() => { onNotifyRef.current = onNotify; }, [onNotify]);

  const onReferralAcceptedRef = useRef(onReferralAccepted);
  useEffect(() => { onReferralAcceptedRef.current = onReferralAccepted; }, [onReferralAccepted]);

  const referralsRef = useRef(referrals);
  useEffect(() => { referralsRef.current = referrals; }, [referrals]);

  const addUnseen = useCallback((id: string, params: { type: 'info' | 'warning'; title: string; message: string; showToast?: boolean }) => {
    setUnseenAttentionIds((prev) => new Set([...prev, id]));
    onNotifyRef.current?.(id, params);
  }, []);

  const markAsSeen = useCallback((id: string) => {
    setUnseenAttentionIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const getById = useCallback(
    (id: string) => referrals.find((r) => r.id === id),
    [referrals]
  );

  const appendTimelineEvent = (
    referral: Referral,
    event: Omit<ReferralTimelineEvent, 'id'>
  ): Referral => ({
    ...referral,
    updatedAt: new Date().toISOString(),
    timeline: [
      ...referral.timeline,
      { ...event, id: `${referral.id}-t${Date.now()}` },
    ],
  });

  const acceptReferral = useCallback((id: string) => {
    const referral = referralsRef.current.find((r) => r.id === id);
    setReferrals((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const now = new Date().toISOString();
        const updated: Referral = {
          ...r,
          stage: ReferralStage.EXECUTION,
          status: ReferralStatus.ACCEPTED,
          finalDecision: DecisionOutcome.ACCEPT,
          finalDecisionBy: 'Current User',
          finalDecisionAt: now,
          updatedAt: now,
          executionSteps: [
            { label: 'EHR Patient Record Created', status: 'COMPLETE', timestamp: now },
            { label: 'Episode of Care Created', status: 'COMPLETE', timestamp: now },
            { label: 'Referral Sent to Operate for Intake Assignment', status: 'COMPLETE', timestamp: now },
            { label: 'Visit(s) Scheduled', status: 'PENDING' },
          ],
        };
        const acceptDetail = r.source === ReferralSource.PHONE && r.callerName
          ? `Referral accepted. Execution started. Decision communicated to ${r.callerName} (${r.callerRelationship}) during intake call.`
          : 'Referral accepted. Execution started.';
        return appendTimelineEvent(updated, {
          timestamp: now,
          type: 'DECISION',
          actor: 'Current User',
          label: 'Decision: Accepted',
          detail: acceptDetail,
        });
      })
    );
    if (referral) onReferralAcceptedRef.current?.(id, referral.patient);
  }, []);

  const declineReferral = useCallback((id: string, reason: string) => {
    setReferrals((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const now = new Date().toISOString();
        const updated: Referral = {
          ...r,
          stage: ReferralStage.EXECUTION,
          status: ReferralStatus.DECLINED,
          finalDecision: DecisionOutcome.DECLINE,
          finalDecisionBy: 'Current User',
          finalDecisionAt: now,
          overrideReason: reason,
          updatedAt: now,
          executionSteps: [
            { label: 'Decline Notification Drafted', status: 'PENDING' },
            { label: 'Outbound Communication Sent', status: 'PENDING' },
          ],
        };
        const declineDetail = r.source === ReferralSource.PHONE && r.callerName
          ? `${reason} — Decision communicated to ${r.callerName} (${r.callerRelationship}) during intake call.`
          : reason;
        return appendTimelineEvent(updated, {
          timestamp: now,
          type: 'DECISION',
          actor: 'Current User',
          label: 'Decision: Declined',
          detail: declineDetail,
        });
      })
    );
  }, []);

  const overrideDecision = useCallback(
    (id: string, outcome: DecisionOutcome, reason: string) => {
      const referral = referralsRef.current.find((r) => r.id === id);
      const isAccept =
        outcome === DecisionOutcome.OVERRIDE_ACCEPT ||
        outcome === DecisionOutcome.ACCEPT;
      setReferrals((prev) =>
        prev.map((r) => {
          if (r.id !== id) return r;
          const now = new Date().toISOString();
          const updated: Referral = {
            ...r,
            stage: ReferralStage.EXECUTION,
            status: isAccept ? ReferralStatus.ACCEPTED : ReferralStatus.DECLINED,
            finalDecision: outcome,
            finalDecisionBy: 'Current User',
            finalDecisionAt: now,
            overrideReason: reason,
            updatedAt: now,
            executionSteps: isAccept
              ? [
                  { label: 'EHR Patient Record Created', status: 'COMPLETE', timestamp: now },
                  { label: 'Episode of Care Created', status: 'COMPLETE', timestamp: now },
                  { label: 'Referral Sent to Operate for Intake Assignment', status: 'COMPLETE', timestamp: now },
                  { label: 'Visit(s) Scheduled', status: 'PENDING' },
                ]
              : [
                  { label: 'Decline Notification Drafted', status: 'PENDING' },
                  { label: 'Outbound Communication Sent', status: 'PENDING' },
                ],
          };
          const overrideDetail = r.source === ReferralSource.PHONE && r.callerName
            ? `${reason} — Decision communicated to ${r.callerName} (${r.callerRelationship}) during intake call.`
            : reason;
          return appendTimelineEvent(updated, {
            timestamp: now,
            type: 'OVERRIDE',
            actor: 'Current User',
            label: `Human override: ${isAccept ? 'Accept' : 'Decline'}`,
            detail: overrideDetail,
          });
        })
      );
      if (isAccept && referral) onReferralAcceptedRef.current?.(id, referral.patient);
    },
    []
  );

  const beginProcessing = useCallback((id: string) => {
    setReferrals((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const now = new Date().toISOString();
        const sourceLabel = r.source === ReferralSource.FAX ? 'fax' : 'phone';
        return appendTimelineEvent(
          { ...r, stage: ReferralStage.PROCESSING, status: ReferralStatus.PROCESSING, updatedAt: now },
          {
            timestamp: now,
            type: 'PROCESSING',
            actor: 'Current User',
            label: 'Processing started by intake coordinator',
            detail: `Coordinator initiated AI extraction for ${sourceLabel} referral`,
          }
        );
      })
    );
  }, []);

  const returnToProcessing = useCallback((id: string, note: string, tasks: ReprocessingTask[]) => {
    setReferrals((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const now = new Date().toISOString();
        return appendTimelineEvent(
          {
            ...r,
            stage: ReferralStage.PROCESSING,
            status: ReferralStatus.PROCESSING,
            processingComplete: false,
            aiRecommendation: DecisionOutcome.PENDING,
            aiRecommendationReason: 'Returned for re-processing. AI will re-evaluate once all flagged items are resolved.',
            reprocessingNote: note,
            reprocessingTasks: tasks,
            updatedAt: now,
          },
          {
            timestamp: now,
            type: 'OVERRIDE',
            actor: 'Current User',
            label: 'Returned to Processing by coordinator',
            detail: note,
          }
        );
      })
    );
  }, []);

  const simulateReprocessingComplete = useCallback((id: string) => {
    setReferrals((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const now = new Date().toISOString();
        const completedTasks = (r.reprocessingTasks ?? []).map((t) => ({
          ...t,
          status: 'COMPLETE' as const,
          completedAt: now,
        }));
        const updated: Referral = {
          ...r,
          stage: ReferralStage.DECISION,
          status: ReferralStatus.RECOMMENDATION_READY,
          processingComplete: true,
          reprocessingTasks: completedTasks,
          aiRecommendation: DecisionOutcome.ACCEPT,
          aiRecommendationReason: 'Re-processing complete. All flagged items have been resolved and criteria re-evaluated. Ready for decision.',
          updatedAt: now,
        };
        return appendTimelineEvent(updated, {
          timestamp: now,
          type: 'PROCESSING',
          actor: 'AI',
          label: 'Re-processing complete — all flagged items resolved',
          detail: 'AI re-evaluated all flagged criteria. Recommendation updated to Accept.',
        });
      })
    );
  }, []);

  const createPhoneReferral = useCallback((data: PhoneIntakeFormData) => {
    const id = `ref-phone-${Date.now()}`;
    const now = new Date().toISOString();

    if (!isInServiceTerritory(data.city)) {
      const reason = `Patient location (${data.city}, ${data.state}) is outside all active service areas.`;
      const autoDeclinedReferral: Referral = {
        id,
        stage: ReferralStage.EXECUTION,
        status: ReferralStatus.AUTO_DECLINED,
        source: ReferralSource.PHONE,
        ingestionType: IngestionType.ASSISTED,
        receivedAt: now,
        updatedAt: now,
        processingComplete: false,
        autoDeclined: true,
        autoDeclineReason: reason,
        outreachStatus: 'PENDING',
        outreachScheduledAt: scheduleOutreachAt(new Date()),
        finalDecision: DecisionOutcome.DECLINE,
        finalDecisionBy: 'System (Auto)',
        finalDecisionAt: now,
        callerName: data.callerName,
        callerRelationship: data.callerRelationship || undefined,
        patient: {
          name: data.patientName,
          dob: data.dob,
          phone: data.phone || undefined,
          address: data.address,
          city: data.city,
          state: data.state,
          zip: data.zip,
          insurance: data.insurance,
          insuranceMemberId: data.insuranceMemberId || undefined,
          diagnosisCodes: data.diagnosisCode ? [data.diagnosisCode] : [],
          diagnosisLabels: [data.diagnosisLabel],
          requestedProgram: data.requestedProgram,
          referringProvider: data.referringProvider,
          referringFacility: data.referringFacility,
          referringPhone: data.referringPhone || undefined,
          referringFax: data.referringFax || undefined,
        },
        summary: `${data.patientName} referred via phone by ${data.callerName}. Automatically declined — ${reason}`,
        flags: [{ id: `${id}-f1`, severity: FlagSeverity.BLOCKING, label: 'Outside active service area', detail: reason }],
        missingFields: [],
        decisionFactors: [
          { criterion: 'Geography', requirement: 'Active service area', patientValue: `${data.city}, ${data.state}`, result: 'FAIL' },
          { criterion: 'Insurance', requirement: 'In-network carrier', patientValue: data.insurance, result: 'PASS' },
          { criterion: 'Program Fit', requirement: `${data.requestedProgram} eligible`, patientValue: data.requestedProgram, result: 'PASS' },
        ],
        capacityInfo: { territory: data.city, available: false, availableCount: 0, recommendation: 'HOLD', recommendationLabel: 'N/A — outside service area' },
        aiRecommendation: DecisionOutcome.DECLINE,
        aiRecommendationReason: reason,
        timeline: [
          { id: `${id}-t1`, timestamp: now, type: 'RECEIVED', actor: 'SYSTEM', label: 'Referral received via Phone', detail: `Intake by ${data.callerName} (${data.callerRelationship}).` },
          { id: `${id}-t2`, timestamp: now, type: 'DECISION', actor: 'SYSTEM', label: 'Auto-declined — outside service area', detail: reason },
        ],
      };
      setReferrals((prev) => [autoDeclinedReferral, ...prev]);
      addUnseen(id, {
        type: 'warning',
        title: 'Auto-Decline Pending Review',
        message: `${data.patientName} — outside service area, outreach pending`,
      });
      return id;
    }

    const newReferral: Referral = {
      id,
      stage: ReferralStage.INTAKE,
      status: ReferralStatus.RECEIVED,
      source: ReferralSource.PHONE,
      ingestionType: IngestionType.ASSISTED,
      receivedAt: now,
      updatedAt: now,
      processingComplete: false,
      patient: {
        name: data.patientName,
        dob: data.dob,
        phone: data.phone || undefined,
        address: data.address,
        city: data.city,
        state: data.state,
        zip: data.zip,
        insurance: data.insurance,
        insuranceMemberId: data.insuranceMemberId || undefined,
        diagnosisCodes: data.diagnosisCode ? [data.diagnosisCode] : [],
        diagnosisLabels: [data.diagnosisLabel],
        requestedProgram: data.requestedProgram,
        referringProvider: data.referringProvider,
        referringFacility: data.referringFacility,
        referringPhone: data.referringPhone || undefined,
        referringFax: data.referringFax || undefined,
      },
      callerName: data.callerName,
      callerRelationship: data.callerRelationship || undefined,
      summary: `${data.patientName} referred for ${data.requestedProgram} via phone intake. Diagnosis: ${data.diagnosisLabel}. Insurance: ${data.insurance}. Referring provider: ${data.referringProvider} at ${data.referringFacility}. Manually entered by intake coordinator.`,
      flags: [
        {
          id: `${id}-f1`,
          severity: FlagSeverity.INFO,
          label: 'Manually entered referral',
          detail: 'Data entered via phone intake. Recommend clinical verification at start of care.',
        },
      ],
      missingFields: [],
      decisionFactors: [],
      capacityInfo: {
        territory: data.city || 'Unknown',
        available: true,
        availableCount: 3,
        recommendation: 'ACCEPT',
        recommendationLabel: `Accept — 3 slots available in ${data.city || 'territory'}`,
      },
      aiRecommendation: DecisionOutcome.PENDING,
      aiRecommendationReason: 'Awaiting AI processing.',
      timeline: [
        {
          id: `${id}-t1`,
          timestamp: now,
          type: 'RECEIVED',
          actor: 'Current User',
          label: 'Referral received via Phone',
          detail: `Phone intake completed by coordinator. Caller: ${data.callerName} (${data.callerRelationship || 'Unknown'}). Referring provider: ${data.referringProvider}.`,
        },
      ],
    };

    setReferrals((prev) => [newReferral, ...prev]);

    // Advance to PROCESSING after a brief delay (simulates submit → AI picks it up)
    setTimeout(() => {
      setReferrals((prev) =>
        prev.map((r) => {
          if (r.id !== id) return r;
          const t = new Date().toISOString();
          return appendTimelineEvent(
            { ...r, stage: ReferralStage.PROCESSING, status: ReferralStatus.PROCESSING, updatedAt: t },
            { timestamp: t, type: 'PROCESSING', actor: 'AI', label: 'AI validation started', detail: 'Validating manually entered data against program criteria' }
          );
        })
      );
    }, 1500);

    // Advance to DECISION with ACCEPT recommendation
    setTimeout(() => {
      setReferrals((prev) =>
        prev.map((r) => {
          if (r.id !== id) return r;
          const t = new Date().toISOString();
          const updated: Referral = {
            ...r,
            stage: ReferralStage.DECISION,
            status: ReferralStatus.RECOMMENDATION_READY,
            processingComplete: true,
            aiRecommendation: DecisionOutcome.ACCEPT,
            aiRecommendationReason: `All required criteria are met for ${data.requestedProgram}. Geography, insurance, and program fit confirmed. Manually entered data is flagged for verification at start of care. Recommend acceptance.`,
            decisionFactors: [
              { criterion: 'Geography', requirement: 'Active service area', patientValue: `${data.city}, ${data.state}`, result: 'PASS' },
              { criterion: 'Insurance', requirement: 'In-network carrier', patientValue: data.insurance, result: 'PASS' },
              { criterion: 'Program Fit', requirement: `${data.requestedProgram} eligible`, patientValue: data.requestedProgram, result: 'PASS' },
              { criterion: 'Clinical Acuity', requirement: 'Stable for home care', patientValue: data.diagnosisLabel, result: 'PASS' },
            ],
            updatedAt: t,
          };
          return appendTimelineEvent(updated, {
            timestamp: t,
            type: 'DECISION',
            actor: 'AI',
            label: 'AI recommendation ready: Accept',
            detail: 'All criteria met. Manual entry note added. Awaiting coordinator confirmation.',
          });
        })
      );
      addUnseen(id, {
        type: 'info',
        title: 'New Referral Ready for Decision',
        message: `${data.patientName} — phone intake complete, ready to review`,
      });
    }, 4500);

    return id;
  }, [addUnseen]);

  const createFaxReferral = useCallback((data: FaxIntakeFormData): string => {
    const id = `ref-fax-${Date.now()}`;
    const now = new Date().toISOString();

    // Territory check — auto-decline immediately if out of service area
    if (!isInServiceTerritory(data.city)) {
      const reason = `Patient location (${data.city}, ${data.state}) is outside all active service areas.`;
      const autoDeclinedReferral: Referral = {
        id,
        stage: ReferralStage.EXECUTION,
        status: ReferralStatus.AUTO_DECLINED,
        source: ReferralSource.FAX,
        ingestionType: IngestionType.ASSISTED,
        receivedAt: now,
        updatedAt: now,
        processingComplete: false,
        autoDeclined: true,
        autoDeclineReason: reason,
        outreachStatus: 'PENDING',
        outreachScheduledAt: scheduleOutreachAt(new Date()),
        finalDecision: DecisionOutcome.DECLINE,
        finalDecisionBy: 'System (Auto)',
        finalDecisionAt: now,
        uploadedFileUrl: data.fileUrl || undefined,
        uploadedFileName: data.fileName || undefined,
        patient: {
          name: data.patientName,
          dob: data.dob,
          phone: data.phone || undefined,
          address: data.address,
          city: data.city,
          state: data.state,
          zip: data.zip,
          insurance: data.insurance,
          insuranceMemberId: data.insuranceMemberId || undefined,
          diagnosisCodes: data.diagnosisCode ? [data.diagnosisCode] : [],
          diagnosisLabels: [data.diagnosisLabel],
          requestedProgram: data.requestedProgram,
          referringProvider: data.referringProvider,
          referringFacility: data.referringFacility,
          referringPhone: data.referringPhone || undefined,
          referringFax: data.referringFax || undefined,
        },
        summary: `${data.patientName} referred for ${data.requestedProgram} via fax. Automatically declined — ${reason}`,
        flags: [{ id: `${id}-f1`, severity: FlagSeverity.BLOCKING, label: 'Outside active service area', detail: reason }],
        missingFields: [],
        decisionFactors: [
          { criterion: 'Geography', requirement: 'Active service area', patientValue: `${data.city}, ${data.state}`, result: 'FAIL' },
          { criterion: 'Insurance', requirement: 'In-network carrier', patientValue: data.insurance, result: 'PASS' },
          { criterion: 'Program Fit', requirement: `${data.requestedProgram} eligible`, patientValue: data.requestedProgram, result: 'PASS' },
        ],
        capacityInfo: { territory: data.city, available: false, availableCount: 0, recommendation: 'HOLD', recommendationLabel: 'N/A — outside service area' },
        aiRecommendation: DecisionOutcome.DECLINE,
        aiRecommendationReason: reason,
        timeline: [
          { id: `${id}-t1`, timestamp: now, type: 'RECEIVED', actor: 'SYSTEM', label: 'Referral received via Fax', detail: `Document uploaded: ${data.fileName}.` },
          { id: `${id}-t2`, timestamp: now, type: 'DECISION', actor: 'SYSTEM', label: 'Auto-declined — outside service area', detail: reason },
        ],
      };
      setReferrals((prev) => [autoDeclinedReferral, ...prev]);
      addUnseen(id, {
        type: 'warning',
        title: 'Auto-Decline Pending Review',
        message: `${data.patientName} — outside service area, outreach pending`,
      });
      return id;
    }

    // Classify missing fields — blocking ones hold the referral in AWAITING_INFO
    const blockingMissing = [
      ...(!data.insuranceMemberId ? [{ field: 'insuranceMemberId', label: 'Insurance Member ID', required: true, aiOutreachSent: false, resolved: false }] : []),
    ];
    const nonBlockingMissing = [
      ...(!data.referringPhone ? [{ field: 'referringPhone', label: 'Referring Provider Phone', required: false, aiOutreachSent: false, resolved: false }] : []),
    ];
    const hasBlocking = blockingMissing.length > 0;

    const newReferral: Referral = {
      id,
      stage: ReferralStage.INTAKE,
      status: ReferralStatus.RECEIVED,
      source: ReferralSource.FAX,
      ingestionType: IngestionType.ASSISTED,
      receivedAt: now,
      updatedAt: now,
      processingComplete: false,
      patient: {
        name: data.patientName,
        dob: data.dob,
        phone: data.phone || undefined,
        address: data.address,
        city: data.city,
        state: data.state,
        zip: data.zip,
        insurance: data.insurance,
        insuranceMemberId: data.insuranceMemberId || undefined,
        diagnosisCodes: data.diagnosisCode ? [data.diagnosisCode] : [],
        diagnosisLabels: [data.diagnosisLabel],
        requestedProgram: data.requestedProgram,
        referringProvider: data.referringProvider,
        referringFacility: data.referringFacility,
        referringPhone: data.referringPhone || undefined,
        referringFax: data.referringFax || undefined,
      },
      summary: `${data.patientName} referred for ${data.requestedProgram} via fax. Diagnosis: ${data.diagnosisLabel}. Insurance: ${data.insurance}. Referring provider: ${data.referringProvider} at ${data.referringFacility}. Data extracted by AI from uploaded document (${data.fileName}) and reviewed by intake coordinator.`,
      flags: [
        {
          id: `${id}-f1`,
          severity: FlagSeverity.INFO,
          label: 'Fax-sourced referral',
          detail: `Data extracted by AI from ${data.fileName} and reviewed by intake coordinator. Recommend clinical verification at start of care.`,
        },
        ...(hasBlocking ? [{
          id: `${id}-f2`,
          severity: FlagSeverity.WARNING,
          label: 'Insurance member ID not extracted',
          detail: data.referringPhone
            ? `Member ID was illegible on the fax document. AI has called ${data.referringProvider} at ${data.referringPhone} and left a voicemail requesting it.`
            : `Member ID was illegible on the fax document. No referring provider phone number is on file — a coordinator must contact ${data.referringFacility} directly to obtain it.`,
        }] : []),
      ],
      missingFields: [...blockingMissing, ...nonBlockingMissing],
      decisionFactors: [
        {
          criterion: 'Geography',
          requirement: 'Active service area',
          patientValue: `${data.city}, ${data.state}`,
          result: 'PASS',
        },
        {
          criterion: 'Insurance',
          requirement: 'In-network carrier',
          patientValue: data.insurance,
          result: 'PASS',
        },
        {
          criterion: 'Insurance Verification',
          requirement: 'Valid member ID on file',
          patientValue: hasBlocking
            ? 'Member ID illegible — awaiting response from referring provider'
            : `${data.insurance} — member ID on file`,
          result: hasBlocking ? 'UNKNOWN' : 'PASS',
        },
        {
          criterion: 'Program Eligibility',
          requirement: 'Eligible diagnosis for requested program',
          patientValue: `${data.diagnosisLabel} → ${data.requestedProgram}`,
          result: 'PASS',
        },
      ],
      capacityInfo: {
        territory: data.city || 'Unknown',
        available: true,
        availableCount: 3,
        recommendation: 'ACCEPT',
        recommendationLabel: `Accept — 3 slots available in ${data.city || 'territory'}`,
      },
      aiRecommendation: DecisionOutcome.PENDING,
      aiRecommendationReason: 'Awaiting AI processing.',
      uploadedFileUrl: data.fileUrl || undefined,
      uploadedFileName: data.fileName || undefined,
      timeline: [
        {
          id: `${id}-t1`,
          timestamp: now,
          type: 'RECEIVED',
          actor: 'SYSTEM',
          label: 'Referral received via Fax',
          detail: `Document uploaded by intake coordinator: ${data.fileName}. AI extraction reviewed and confirmed.`,
        },
      ],
    };

    setReferrals((prev) => [newReferral, ...prev]);

    // 1.5s → PROCESSING
    setTimeout(() => {
      setReferrals((prev) =>
        prev.map((r) => {
          if (r.id !== id) return r;
          const t = new Date().toISOString();
          return appendTimelineEvent(
            { ...r, stage: ReferralStage.PROCESSING, status: ReferralStatus.PROCESSING, updatedAt: t },
            { timestamp: t, type: 'PROCESSING', actor: 'AI', label: 'AI validation started', detail: 'Validating extracted data against program criteria' }
          );
        })
      );
    }, 1500);

    if (hasBlocking) {
      // 3s → AWAITING_INFO
      setTimeout(() => {
        const canAutoOutreach = !!data.referringPhone;
        setReferrals((prev) =>
          prev.map((r) => {
            if (r.id !== id) return r;
            const t = new Date().toISOString();
            const updatedMissing = r.missingFields.map((f) =>
              canAutoOutreach && blockingMissing.some((b) => b.field === f.field)
                ? { ...f, aiOutreachSent: true, outreachTimestamp: t }
                : f
            );
            const updated: Referral = {
              ...r,
              status: ReferralStatus.AWAITING_INFO,
              missingFields: updatedMissing,
              updatedAt: t,
            };
            return appendTimelineEvent(updated, canAutoOutreach ? {
              timestamp: t,
              type: 'OUTREACH',
              actor: 'AI',
              label: 'AI called referring provider — voicemail left',
              detail: `Automated call placed to ${data.referringProvider} at ${data.referringPhone} (${data.referringFacility}). Voicemail left requesting Insurance Member ID for ${data.insurance}. Awaiting callback.`,
            } : {
              timestamp: t,
              type: 'FLAG',
              actor: 'SYSTEM',
              label: 'Manual outreach required',
              detail: `Insurance member ID is missing and no referring provider phone number is on file. A coordinator must contact ${data.referringFacility} directly to obtain it before this referral can proceed.`,
            });
          })
        );
        addUnseen(id, {
          type: 'warning',
          title: 'Referral Needs Attention',
          message: data.referringPhone
            ? `${data.patientName} — missing information, AI left voicemail for ${data.referringProvider}`
            : `${data.patientName} — missing information, manual outreach required (no phone on file)`,
          showToast: false,
        });
      }, 3000);
      // Referral stays in AWAITING_INFO until simulateMissingInfoResponse is triggered
    } else {
      // No blocking fields → advance directly to DECISION
      setTimeout(() => {
        setReferrals((prev) =>
          prev.map((r) => {
            if (r.id !== id) return r;
            const t = new Date().toISOString();
            const updated: Referral = {
              ...r,
              stage: ReferralStage.DECISION,
              status: ReferralStatus.RECOMMENDATION_READY,
              processingComplete: true,
              aiRecommendation: DecisionOutcome.ACCEPT,
              aiRecommendationReason: `All required criteria are met for ${data.requestedProgram}. Geography, insurance, and program fit confirmed. Fax-sourced data reviewed by coordinator. Recommend acceptance.`,
              decisionFactors: [
                { criterion: 'Geography', requirement: 'Active service area', patientValue: `${data.city}, ${data.state}`, result: 'PASS' },
                { criterion: 'Insurance', requirement: 'In-network carrier + verified member ID', patientValue: data.insurance, result: 'PASS' },
                { criterion: 'Program Fit', requirement: `${data.requestedProgram} eligible`, patientValue: data.requestedProgram, result: 'PASS' },
                { criterion: 'Clinical Acuity', requirement: 'Stable for home care', patientValue: data.diagnosisLabel, result: 'PASS' },
              ],
              updatedAt: t,
            };
            return appendTimelineEvent(updated, {
              timestamp: t,
              type: 'DECISION',
              actor: 'AI',
              label: 'AI recommendation ready: Accept',
              detail: 'All criteria met. Coordinator-reviewed extraction. Awaiting confirmation.',
            });
          })
        );
        addUnseen(id, {
          type: 'info',
          title: 'New Referral Ready for Decision',
          message: `${data.patientName} — fax intake complete, ready to review`,
        });
      }, 4500);
    }

    return id;
  }, [addUnseen]);

  const simulateIntake = useCallback((source: ReferralSource) => {
    const newReferral = buildSimulatedReferral(source);
    setReferrals((prev) => [newReferral, ...prev]);

    // Auto-advance to PROCESSING for channels that don't require human intervention
    const isAutoChannel =
      source === ReferralSource.EMAIL ||
      source === ReferralSource.EFAX ||
      source === ReferralSource.API;

    if (isAutoChannel) {
      setTimeout(() => {
        setReferrals((prev) =>
          prev.map((r) => {
            if (r.id !== newReferral.id) return r;
            const now = new Date().toISOString();
            return appendTimelineEvent(
              { ...r, stage: ReferralStage.PROCESSING, status: ReferralStatus.PROCESSING, updatedAt: now },
              {
                timestamp: now,
                type: 'PROCESSING',
                actor: 'AI',
                label: 'AI extraction started',
                detail: 'Auto-ingested — no human intervention required',
              }
            );
          })
        );
      }, 1500);

      // Advance to DECISION with populated decision factors
      setTimeout(() => {
        setReferrals((prev) =>
          prev.map((r) => {
            if (r.id !== newReferral.id) return r;
            const now = new Date().toISOString();
            const updated: Referral = {
              ...r,
              stage: ReferralStage.DECISION,
              status: ReferralStatus.RECOMMENDATION_READY,
              processingComplete: true,
              aiRecommendation: DecisionOutcome.ACCEPT,
              aiRecommendationReason:
                'All required criteria are met. Geography, insurance, and program fit confirmed. No missing information. Recommend acceptance.',
              decisionFactors: [
                { criterion: 'Geography', requirement: 'Active service area', patientValue: 'Denver, CO', result: 'PASS' },
                { criterion: 'Insurance', requirement: 'In-network carrier', patientValue: 'Aetna', result: 'PASS' },
                { criterion: 'Program Fit', requirement: 'Home Health eligible', patientValue: 'Home Health', result: 'PASS' },
                { criterion: 'Clinical Acuity', requirement: 'Stable for home care', patientValue: 'Stable', result: 'PASS' },
              ],
              updatedAt: now,
            };
            return appendTimelineEvent(updated, {
              timestamp: now,
              type: 'DECISION',
              actor: 'AI',
              label: 'AI recommendation ready: Accept',
              detail: 'All criteria met. Awaiting coordinator confirmation.',
            });
          })
        );
      addUnseen(newReferral.id, {
        type: 'info',
        title: 'New Referral Ready for Decision',
        message: `${newReferral.patient.name} — all criteria met, ready to review`,
      });
      }, 4500);
    }
    // FAX and PHONE stay in INTAKE — a coordinator must manually begin processing
  }, [addUnseen]);

  const simulateMissingInfoResponse = useCallback((referralId: string) => {
    let patientName = '';
    setReferrals((prev) =>
      prev.map((r) => {
        if (r.id !== referralId) return r;
        patientName = r.patient.name;
        const now = new Date().toISOString();
        const resolvedFields = r.missingFields.map((f) => ({
          ...f,
          resolved: true,
          resolvedValue: f.field === 'insuranceMemberId' ? '1EG4-TE5-MK72' : '(206) 555-0199',
        }));
        const resolvedMemberId = resolvedFields.find((f) => f.field === 'insuranceMemberId')?.resolvedValue;
        const resolvedFactors = r.decisionFactors.map((f) => {
          if (f.result !== 'UNKNOWN') return f;
          if (f.criterion === 'Insurance Verification') {
            return {
              ...f,
              patientValue: `${r.patient.insurance} — member ID verified (${resolvedMemberId ?? 'on file'})`,
              result: 'PASS' as const,
            };
          }
          return { ...f, result: 'PASS' as const };
        });
        const updated: Referral = {
          ...r,
          missingFields: resolvedFields,
          decisionFactors: resolvedFactors,
          processingComplete: true,
          stage: ReferralStage.DECISION,
          status: ReferralStatus.RECOMMENDATION_READY,
          aiRecommendation: DecisionOutcome.ACCEPT,
          aiRecommendationReason:
            'All missing information has been received. All criteria are now met. Ready for acceptance.',
          updatedAt: now,
        };
        const hadAiOutreach = r.missingFields.some((f) => f.aiOutreachSent);
        return appendTimelineEvent(
          appendTimelineEvent(updated, {
            timestamp: now,
            type: 'RESPONSE',
            actor: hadAiOutreach ? 'AI' : 'SYSTEM',
            label: hadAiOutreach
              ? 'Referring provider returned AI\'s call — information received'
              : 'Missing information received via manual outreach',
            detail: hadAiOutreach
              ? 'Callback received from referring provider\'s office. Insurance member ID provided and verified.'
              : 'Coordinator obtained missing information from referring facility. Record updated.',
          }),
          {
            timestamp: now,
            type: 'PROCESSING',
            actor: 'AI',
            label: 'Re-processing complete — all fields resolved',
            detail: 'AI recommendation updated to Accept.',
          }
        );
      })
    );
    addUnseen(referralId, {
      type: 'info',
      title: 'Missing Information Resolved',
      message: `${patientName} — information received, ready for decision`,
    });
  }, [addUnseen]);

  const simulateExecution = useCallback((referralId: string) => {
    setReferrals((prev) =>
      prev.map((r) => {
        if (r.id !== referralId || !r.executionSteps) return r;
        const now = new Date().toISOString();
        const steps = [...r.executionSteps];
        // Find first non-COMPLETE step and advance it
        const nextIndex = steps.findIndex((s) => s.status !== 'COMPLETE');
        if (nextIndex === -1) return r;

        if (steps[nextIndex].status === 'PENDING') {
          steps[nextIndex] = { ...steps[nextIndex], status: 'IN_PROGRESS' };
        } else if (steps[nextIndex].status === 'IN_PROGRESS') {
          steps[nextIndex] = { ...steps[nextIndex], status: 'COMPLETE', timestamp: now };
          // Start next step if exists
          if (nextIndex + 1 < steps.length) {
            steps[nextIndex + 1] = { ...steps[nextIndex + 1], status: 'IN_PROGRESS' };
          }
        }

        const updated: Referral = { ...r, executionSteps: steps, updatedAt: now };
        return appendTimelineEvent(updated, {
          timestamp: now,
          type: 'EXECUTION',
          actor: 'SYSTEM',
          label: `Execution step: ${steps[nextIndex].label}`,
          detail: steps[nextIndex].status === 'COMPLETE' ? 'Completed' : 'In progress',
        });
      })
    );
  }, []);

  const simulateIncomingDocument = useCallback((referralId: string) => {
    let patientName = '';
    let raisedFlag = false;
    setReferrals((prev) =>
      prev.map((r) => {
        if (r.id !== referralId) return r;
        patientName = r.patient.name;
        const existing = r.additionalDocuments ?? [];
        const templateIndex = existing.length;
        if (templateIndex >= INCOMING_DOC_TEMPLATES.length) return r;
        const template = INCOMING_DOC_TEMPLATES[templateIndex];
        const now = new Date().toISOString();
        const newDoc: ReferralDocument = {
          id: `${referralId}-doc-${templateIndex + 1}`,
          type: template.type,
          fileName: template.fileName,
          receivedAt: now,
          source: template.source,
          matchConfidence: template.matchConfidence,
          matchedOn: template.matchedOn,
          simulatedContent: template.buildContent(r),
          isNew: true,
        };

        let updated: Referral = {
          ...r,
          additionalDocuments: [...existing, newDoc],
          updatedAt: now,
        };

        // Document received event always comes first
        updated = appendTimelineEvent(updated, {
          timestamp: now,
          type: 'RECEIVED',
          actor: 'SYSTEM',
          label: `Incoming document matched: ${template.type}`,
          detail: `${template.fileName} received via ${template.source}. Matched with ${template.matchConfidence.toLowerCase()} confidence on: ${template.matchedOn.join(', ')}.`,
        });

        // Wound Assessment — elevated clinical complexity
        if (templateIndex === 1) {
          raisedFlag = true;
          const woundFlag = {
            id: `${referralId}-flag-wound`,
            severity: FlagSeverity.WARNING,
            label: 'Elevated clinical complexity — wound assessment requires review',
            detail: 'Wound assessment indicates full-thickness wound with tunneling and MRSA-positive culture. Clinical picture may exceed standard home health scope. Coordinator and clinical review required before proceeding.',
          };
          const needsHold = updated.status === ReferralStatus.RECOMMENDATION_READY;
          updated = {
            ...updated,
            flags: [...updated.flags, woundFlag],
            ...(needsHold ? {
              status: ReferralStatus.PENDING_REVIEW,
              aiRecommendation: DecisionOutcome.PENDING,
              aiRecommendationReason: 'Wound assessment received after initial recommendation indicates elevated clinical complexity (MRSA, full-thickness wound with tunneling). Prior recommendation placed on hold — clinical review required before a decision can be made.',
            } : {}),
          };
          updated = appendTimelineEvent(updated, {
            timestamp: now,
            type: 'FLAG',
            actor: 'AI',
            label: 'Flag raised — wound complexity exceeds initial documentation',
            detail: 'AI recommendation placed on hold. Wound assessment indicates MRSA-positive culture and full-thickness tunneling wound. Standard home health wound care protocols may be insufficient.',
          });
        }

        // Discharge Summary — address discrepancy
        if (templateIndex === 2) {
          raisedFlag = true;
          const addressFlag = {
            id: `${referralId}-flag-address`,
            severity: FlagSeverity.BLOCKING,
            label: 'Discharge address may be outside active service area',
            detail: 'Discharge summary lists recovery address as 5220 Mesa Dr, Pueblo, CO 81003 — different from the referral address and outside all active service territories. Confirm final discharge location before accepting.',
          };
          const updatedFactors = updated.decisionFactors.map((f) =>
            f.criterion === 'Geography' || f.criterion === 'Service Area'
              ? { ...f, patientValue: 'Discharge to Pueblo, CO (outside service area)', result: 'FAIL' as const }
              : f
          );
          const needsHold =
            updated.status === ReferralStatus.RECOMMENDATION_READY ||
            updated.status === ReferralStatus.PENDING_REVIEW;
          updated = {
            ...updated,
            flags: [...updated.flags, addressFlag],
            decisionFactors: updatedFactors,
            ...(needsHold ? {
              status: ReferralStatus.PENDING_REVIEW,
              aiRecommendation: DecisionOutcome.PENDING,
              aiRecommendationReason: 'Discharge summary indicates patient will recover at a different address outside the active service area (Pueblo, CO 81003). Geography criterion cannot be confirmed. Coordinator must verify discharge location before any decision can be made.',
            } : {}),
          };
          updated = appendTimelineEvent(updated, {
            timestamp: now,
            type: 'FLAG',
            actor: 'AI',
            label: 'Flag raised — discharge address conflicts with referral address',
            detail: 'Discharge summary lists Pueblo, CO as recovery location, which is outside all active service territories. Prior geography pass reversed pending coordinator confirmation.',
          });
        }

        return updated;
      })
    );
    if (patientName) {
      addUnseen(referralId, {
        type: raisedFlag ? 'warning' : 'info',
        title: raisedFlag ? 'Referral Needs Attention' : 'Document Received',
        message: raisedFlag
          ? `${patientName} — new document raised a flag, review required`
          : `${patientName} — new document matched to referral`,
        showToast: false,
      });
    }
  }, [addUnseen]);

  const overrideAutoDecline = useCallback((id: string, reason: string) => {
    const referral = referralsRef.current.find((r) => r.id === id);
    setReferrals((prev) =>
      prev.map((r) => {
        if (r.id !== id || !r.autoDeclined) return r;
        const now = new Date().toISOString();
        const wasOutreachSent = r.outreachStatus === 'SENT';
        const updated: Referral = {
          ...r,
          autoDeclined: false,
          autoDeclineReason: undefined,
          outreachStatus: undefined,
          outreachScheduledAt: undefined,
          outreachSentAt: undefined,
          stage: ReferralStage.EXECUTION,
          status: ReferralStatus.ACCEPTED,
          finalDecision: DecisionOutcome.OVERRIDE_ACCEPT,
          finalDecisionBy: 'Coordinator',
          finalDecisionAt: now,
          overrideReason: reason,
          updatedAt: now,
          executionSteps: [
            { label: 'EHR Patient Record Created', status: 'COMPLETE', timestamp: now },
            { label: 'Episode of Care Created', status: 'COMPLETE', timestamp: now },
            { label: 'Referral Sent to Operate for Intake Assignment', status: 'COMPLETE', timestamp: now },
            { label: 'Visit(s) Scheduled', status: 'PENDING' },
          ],
        };
        const withOverride = appendTimelineEvent(updated, {
          timestamp: now,
          type: 'OVERRIDE',
          actor: 'Coordinator',
          label: 'Auto-decline overridden — referral accepted',
          detail: reason,
        });
        if (wasOutreachSent) {
          return appendTimelineEvent(withOverride, {
            timestamp: now,
            type: 'EXECUTION',
            actor: 'SYSTEM',
            label: 'Correction notice sent to referring provider',
            detail: 'Referring provider notified that the earlier decline has been superseded.',
          });
        }
        return withOverride;
      })
    );
    if (referral) onReferralAcceptedRef.current?.(id, referral.patient);
  }, []);

  const simulateOutreachSent = useCallback((id: string) => {
    setReferrals((prev) =>
      prev.map((r) => {
        if (r.id !== id || r.outreachStatus !== 'PENDING') return r;
        const now = new Date().toISOString();
        const updated: Referral = {
          ...r,
          outreachStatus: 'SENT',
          outreachSentAt: now,
          updatedAt: now,
        };
        return appendTimelineEvent(updated, {
          timestamp: now,
          type: 'EXECUTION',
          actor: 'SYSTEM',
          label: 'Decline outreach sent',
          detail: `Decline notification sent to ${r.patient.referringFacility}.`,
        });
      })
    );
  }, []);

  const resetReferral = useCallback((id: string) => {
    setReferrals((prev) => {
      const original = MOCK_REFERRALS.find((r) => r.id === id);
      if (original) {
        return prev.map((r) => (r.id === id ? original : r));
      }
      // Simulated referrals — just remove them
      return prev.filter((r) => r.id !== id);
    });
  }, []);

  const resetAll = useCallback(() => {
    setReferrals(MOCK_REFERRALS);
  }, []);

  return (
    <TransitionContext.Provider
      value={{
        referrals,
        getById,
        beginProcessing,
        returnToProcessing,
        simulateReprocessingComplete,
        acceptReferral,
        declineReferral,
        overrideDecision,
        createPhoneReferral,
        createFaxReferral,
        simulateIntake,
        simulateMissingInfoResponse,
        simulateExecution,
        simulateIncomingDocument,
        overrideAutoDecline,
        simulateOutreachSent,
        resetReferral,
        resetAll,
        unseenAttentionIds,
        markAsSeen,
        demoUsers: TRANSITION_DEMO_USERS,
        currentDemoUser,
        switchDemoUser,
      }}
    >
      {children}
    </TransitionContext.Provider>
  );
};
